import React, { useContext, useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';
import { withRouter } from 'react-router-dom';
import { WizardPage, wizardProps, WizardPageTemplate } from 'gw-portals-wizard-react';
import {
    ViewModelServiceContext,
    withViewModelService,
    ViewModelForm
} from 'gw-portals-viewmodel-react';
import { TranslatorContext } from '@jutro/locale';
import { ModalNextProvider } from '@jutro/components';

import { useValidation } from 'gw-portals-validation-react';
import { fnolCommonMessages } from 'gw-capability-fnol-common-react';
import { messages as commonMessages } from 'gw-platform-translations';
import metadata from './InjuryPage.metadata.json5';
import styles from './InjuryPage.module.scss';
import messages from '../../FNOLWC7.messages';

function WC7InjuryPage(props) {
    const {
        wizardData: claimVM,
        wizardSnapshot,
        updateWizardData,
        history: {
            location: { state }
        }
    } = props;
    const translator = useContext(TranslatorContext);
    const viewModelService = useContext(ViewModelServiceContext);
    const {
        onValidate,
        isComponentValid
    } = useValidation('WCInjuryPage');
    const handleMedicalAttention = useCallback(
        (value, path) => {
            const updatedClaimVM = viewModelService.create(
                claimVM.value,
                'cc',
                'com.tdic.edge.capabilities.claim.fnol.dto.FnolDTO_Ext',
                {
                    InjuryDetail: true
                }
            );
            const newClaimVM = viewModelService.clone(updatedClaimVM);
            if (_.isNil(_.get(wizardSnapshot.value, 'lobs.wc7workersComp_Ext.firstIntakeDoctor'))) {
                _.set(newClaimVM.value, 'lobs.wc7workersComp_Ext.firstIntakeDoctor', null);
            }
            if (value) {
                _.set(newClaimVM, path, value);
                return updateWizardData(newClaimVM);
            }
            _.set(newClaimVM, path, value);
            return updateWizardData(newClaimVM);
        },
        [claimVM, updateWizardData, viewModelService, wizardSnapshot]
    );


    const handleAddAnInjury = useCallback(() => {
        claimVM.lobs.wc7workersComp_Ext.value.addBodyPartDetails();
        updateWizardData(claimVM);
    }, [claimVM, updateWizardData]);

    const handleInitialAddAnInjury = useCallback(() => {
        if (_.get(claimVM, 'lobs.wc7workersComp_Ext.injuryIncident.bodyParts', []).length === 0) {
            handleAddAnInjury();
        }
    }, [claimVM, handleAddAnInjury]);

    useEffect(
        () => {
            const deathReport = _.get(claimVM.value, 'lobs.wc7workersComp_Ext.deathReport', false);
            _.set(claimVM, 'lobs.wc7workersComp_Ext.deathReport', deathReport);

            const medicalReport = _.get(claimVM.value, 'lobs.wc7workersComp_Ext.medicalReport', false);
            _.set(claimVM, 'lobs.wc7workersComp_Ext.medicalReport', medicalReport);

            const nextClaimVM = viewModelService.create(
                claimVM.value,
                'cc',
                'com.tdic.edge.capabilities.claim.fnol.dto.FnolDTO_Ext',
                {
                    InjuryDetailsValidation: true,
                    InjuryDetail: true
                }
            );
            updateWizardData(nextClaimVM);
            // eslint-disable-next-line no-use-before-define
            handleInitialAddAnInjury();
            if (!medicalReport) {
                handleMedicalAttention(false, 'lobs.wc7workersComp_Ext.medicalReport');
            }
        },
        // only execute once
        // eslint-disable-next-line react-hooks/exhaustive-deps
        []
    );

    const handleRemoveAnInjury = useCallback(
        (event) => {
            const basePath = event.path.replace('lobs.wc7workersComp_Ext', '');
            const tabindex = parseInt(basePath.match(/[0-9]/gi), 10);
            const selectedObj = claimVM.lobs.wc7workersComp_Ext
                .injuryIncident.bodyParts.value[tabindex];
            ModalNextProvider.showConfirm({
                title: messages.wcInjuryRemoveBodyInjury,
                message: translator(messages.wcInjuryModalRemoveBodyInjury, {
                    bodyPartDetail: tabindex + 1
                }),
                status: 'warning',
                icon: 'mi-error-outline',
                confirmButtonText: messages.wcInjuryRemoveButton,
                cancelButtonText: commonMessages.cancelMode
            }).then((results) => {
                if (results === 'cancel') {
                    return _.noop();
                }
                claimVM.lobs.wc7workersComp_Ext.value.removeBodyPartDetails(selectedObj);
                return updateWizardData(claimVM);
            }, _.noop);
        },
        [claimVM, translator, updateWizardData]
    );

    const isVisibleInjuryBodyPartDescription = useCallback(
        (index) => {
            const bodyPartsPath = `lobs.wc7workersComp_Ext.injuryIncident.bodyParts.children[${index}]`;
            const detailedBodyPartDescAvailableVM = _.get(claimVM, bodyPartsPath);
            return (
                detailedBodyPartDescAvailableVM !== undefined
                && !_.isEmpty(_.get(detailedBodyPartDescAvailableVM, 'detailedBodyPartDesc.aspects.availableValues'))
            );
        },
        [claimVM]
    );

    const isVisibleInjuryBodyPartSide = useCallback(
        (index) => {
            const bodyPartsSideOfBodyPath = `lobs.wc7workersComp_Ext.injuryIncident.bodyParts.children[${index}].sideOfBody`;
            const bodyPartsSideOfBodyVM = _.get(claimVM, bodyPartsSideOfBodyPath);
            return bodyPartsSideOfBodyVM !== undefined && bodyPartsSideOfBodyVM.aspects.required;
        },
        [claimVM]
    );

    const onChangeInjuryBodyPart = useCallback(
        (value, index) => {
            const bodyPartPath = `lobs.wc7workersComp_Ext.injuryIncident.bodyParts.children[${index}]`;
            _.set(claimVM, `${bodyPartPath}.sideOfBody`, undefined);
            _.set(claimVM, `${bodyPartPath}.detailedBodyPartDesc`, undefined);
            _.set(claimVM, `${bodyPartPath}.detailedBodyPart`, value);
            updateWizardData(claimVM);
        },
        [claimVM, updateWizardData]
    );

    const generateAddInjuryOverrides = useCallback(() => {
        const overrides = !_.isEmpty(claimVM.lobs.wc7workersComp_Ext.injuryIncident.bodyParts)
        && claimVM.lobs.wc7workersComp_Ext.injuryIncident.bodyParts.children.map(
            (injured, index) => {
                return {
                    [`addInjuryTitleText${index}`]: {
                        value: translator(messages.wcInjuryBodyArea, {
                            bodyAreaCount: index + 1
                        })
                    },
                    [`removeInjuryTitleIcon${index}`]: {
                        visible:
                            claimVM.lobs.wc7workersComp_Ext
                                .injuryIncident.bodyParts.value.length !== 1
                    },
                    [`wcInjuryBodyPart${index}`]: {
                        onValueChange: (value) => {
                            onChangeInjuryBodyPart(value, index);
                        }
                    }
                };
            }
        );

        return Object.assign({}, ...overrides);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [claimVM, onChangeInjuryBodyPart, isVisibleInjuryBodyPartDescription,
        isVisibleInjuryBodyPartSide, translator]);
    const overrideProps = {
        '@field': {
            labelPosition: 'left',
            showOptional: true
        },
        wcInjuryMedicalAttentionRequired: {
            onValueChange: handleMedicalAttention
        },
        wcInjuryResultInDeath: {
            visible: !_.get(claimVM.value, 'lobs.wc7workersComp_Ext.incidentReport')
        },
        ...generateAddInjuryOverrides()
    };

    const resolvers = {
        resolveClassNameMap: styles,
        resolveCallbackMap: {
            handleAddAnInjury: handleAddAnInjury,
            handleRemoveAnInjury: handleRemoveAnInjury,
            handleMedicalAttention: handleMedicalAttention
        }
    };

    const { claimStatus } = state || {};

    const validateClaimStatus = useCallback(() => {
        return (
            !_.isNil(claimStatus)
            && !!_.get(claimVM, 'lossLocation.city.value')
            && claimStatus === translator({ id: 'typekey.ClaimState.draft', defaultMessage: 'Draft' })
        );
    }, [claimStatus, claimVM, translator]);

    const validateInjuryCheck = useCallback(() => {
        return claimVM.aspects.valid && claimVM.aspects.subtreeValid && validateClaimStatus();
    }, [claimVM.aspects.subtreeValid, claimVM.aspects.valid, validateClaimStatus]);

    return (
        <WizardPage
            cancelLabel={translator(fnolCommonMessages.fnolSaveandExit)}
            skipWhen={() => isComponentValid && validateInjuryCheck()}
            disableNext={!isComponentValid}
            template={WizardPageTemplate}
        >
            <ViewModelForm
                uiProps={metadata.pageContent}
                model={claimVM}
                overrideProps={overrideProps}
                onModelChange={updateWizardData}
                onValidationChange={onValidate}
                classNameMap={resolvers.resolveClassNameMap}
                callbackMap={resolvers.resolveCallbackMap}
            />
        </WizardPage>
    );
}

WC7InjuryPage.propTypes = {
    viewModelService: PropTypes.shape({
        create: PropTypes.func
    }).isRequired
};

WC7InjuryPage.propTypes = wizardProps;
export default withRouter(withViewModelService(WC7InjuryPage));
